import * as React from 'react';
import { renderToString } from 'react-dom/server';

import EmptyState from '../empty_state'
import { accountImg, helpImg, generateRecommendations, proficientDiagnostic, STARTER, INTERMEDIATE, ADVANCED, TROUBLE_PROCESSING_MESSAGE, NO_CLASSROOM, TABLE_WIDTH_PROPS, } from '../shared'
import { StudentHistoryInterface, ClassroomInterface, LeveledAssignmentsInterface, DiagnosticsAssignedInterface, } from '../interfaces'
import { Spinner, DataTable, Tooltip, Checkbox, LightButtonLoadingSpinner, EXTRA_SMALL_ICON_BASE_SRC, SMALL_ICON_BASE_SRC, Snackbar, defaultSnackbarTimeout, pluralize, } from '../../../../../Shared/index'
import { requestPost, } from '../../../../../../modules/request'
import useStickyTableHeader from '../../../../../Shared/hooks/useStickyTableHeader'
import useScaledTableColumnWidths from '../../../../../Shared/hooks/useScaledTableColumnWidths'
import useSnackbarMonitor from '../../../../../Shared/hooks/useSnackbarMonitor'

const assignedDiagnosticImg = <img alt="Checkmark indicating the student has been assigned this diagnostic in this classroom" className="assigned-diagnostic-img" src={`${EXTRA_SMALL_ICON_BASE_SRC}/check-small.svg`} />
const demonstratedProficiencyImg = <img alt="Checkmark indicating the student has demonstrated proficiency on all diagnostics" className="demonstrated-proficiency-img" src={`${EXTRA_SMALL_ICON_BASE_SRC}/check-small.svg`} />
const quillRecommendedImg = <img alt="Circle with a quill in it indicating a recommended diagnostic" className="recommended-diagnostic-img" src={`${SMALL_ICON_BASE_SRC}/quill-recommended.svg`} />

interface RecommendationProps {
  classroom: ClassroomInterface;
  getPreviouslyAssignedDiagnosticDataForClassroom: () => void;
  useELLDiagnostics: boolean;
  showEmptyState: boolean;
  studentHistories: StudentHistoryInterface[],
}

function keyForPreviouslyAssignedDiagnostics(diagnosticLevel, useELLDiagnostics) {
  return useELLDiagnostics ? `ell_${diagnosticLevel}` : diagnosticLevel
}

interface StudentDiagnosticCellProps {
  studentHistory: StudentHistoryInterface;
  diagnosticLevel: typeof STARTER | typeof INTERMEDIATE | typeof ADVANCED;
  recommendations: LeveledAssignmentsInterface;
  selectedAssignments: LeveledAssignmentsInterface;
  setSelectedAssignments: React.Dispatch<React.SetStateAction<LeveledAssignmentsInterface>>;
  previouslyAssignedDiagnosticsByLevel: DiagnosticsAssignedInterface;
  useELLDiagnostics: boolean;
  classroom: ClassroomInterface;
}

const StudentDiagnosticCell = ({ studentHistory, diagnosticLevel, recommendations, selectedAssignments, setSelectedAssignments, previouslyAssignedDiagnosticsByLevel, useELLDiagnostics, classroom, }: StudentDiagnosticCellProps) => {
  const { user_id, } = studentHistory

  const recommendation = recommendations[diagnosticLevel]
  const selectedAssignment = selectedAssignments[diagnosticLevel]

  const diagnosticLevelForPreviousAssignments = keyForPreviouslyAssignedDiagnostics(diagnosticLevel, useELLDiagnostics)

  const previousAssignmentsForLevel = previouslyAssignedDiagnosticsByLevel[diagnosticLevelForPreviousAssignments]
  const previousAssignmentRecords = previousAssignmentsForLevel?.filter(record => record.user_id === studentHistory.user_id) || []

  const previousAssignmentRecordToUse = previousAssignmentRecords.find(record => record.completed_at) || previousAssignmentRecords[0]

  const studentIsRecommendedThisDiagnostic = recommendation.student_ids.includes(user_id)
  const studentHasThisDiagnosticSelected = selectedAssignment.student_ids.includes(user_id)

  function toggleAssignment() {
    const newStudentIds = studentHasThisDiagnosticSelected ? selectedAssignment.student_ids.filter(id => id !== user_id) : selectedAssignment.student_ids.concat([user_id])
    const newSelectedAssignments = {...selectedAssignments, [diagnosticLevel]: {...selectedAssignment, student_ids: newStudentIds} }
    setSelectedAssignments(newSelectedAssignments)
  }

  const { completed_at, activity_id, } = previousAssignmentRecordToUse || {}

  const viewResultsHref = `/teachers/progress_reports/diagnostic_reports/#/diagnostics/${activity_id}/classroom/${classroom.id}/responses/${user_id}`

  return (
    <span className="student-diagnostic-cell">
      <span>
        {previousAssignmentRecordToUse ? <React.Fragment>{assignedDiagnosticImg} Assigned</React.Fragment> : <Checkbox onClick={toggleAssignment} selected={studentHasThisDiagnosticSelected} />}
        {studentIsRecommendedThisDiagnostic && quillRecommendedImg}
      </span>
      {completed_at && <a className="quill-button outlined extra-small grey focus-on-light" href={viewResultsHref} rel="noopener noreferrer" target="_blank">View results</a>}
    </span>
  )
}

const Recommendations = ({ classroom, getPreviouslyAssignedDiagnosticDataForClassroom, previouslyAssignedDiagnosticsByLevel, useELLDiagnostics, studentHistories, showEmptyState, }: RecommendationProps) => {
  const [recommendations, setRecommendations] = React.useState<LeveledAssignmentsInterface>(null)
  const [selectedAssignments, setSelectedAssignments] = React.useState<LeveledAssignmentsInterface>(null)
  const [assigning, setAssigning] = React.useState<boolean>(false)
  const [showSnackbar, setShowSnackbar] = React.useState(false);
  const [snackbarText, setSnackbarText] = React.useState('');

  useSnackbarMonitor(showSnackbar, setShowSnackbar, defaultSnackbarTimeout)

  React.useEffect(() => {
    if (!studentHistories) { return }

    const recommendedDiagnostics = generateRecommendations(useELLDiagnostics, studentHistories, classroom.id)
    setSelectedAssignments(recommendedDiagnostics)
    setRecommendations(recommendedDiagnostics)
  }, [studentHistories])

  const dataHasNotLoaded = (
    !studentHistories ||
    !recommendations ||
    !previouslyAssignedDiagnosticsByLevel
  )

  const containerRef = React.useRef<HTMLElement>(null);
  const tableWrapperRef = React.useRef<HTMLDivElement>(null);
  const tableRef = React.useRef<HTMLTableElement>(null);

  const columnWidths = useScaledTableColumnWidths({
    columnWidths: [
      { width: 351 },
      { width: 277, minWidth: 230 },
      { width: 277, minWidth: 230 },
      { width: 277, minWidth: 230 },
    ],
    containerRef,
    ...TABLE_WIDTH_PROPS,
  });

  const headers = (ell) => {
    const ellString = ell ? 'ELL ' : ''
    return [
      {
        name: 'Student',
        width: columnWidths[0],
        attribute: 'student',
        isSortable: true,
        sortAttribute: 'nameForSort'
      },
      {
        name: `${ellString}Starter Diagnostic`,
        width: columnWidths[1],
        attribute: STARTER,
        noTooltip: true
      },
      {
        name: `${ellString}Intermediate Diagnostic`,
        width: columnWidths[2],
        attribute: INTERMEDIATE,
        noTooltip: true
      },
      {
        name: `${ellString}Advanced Diagnostic`,
        width: columnWidths[3],
        attribute: ADVANCED,
        noTooltip: true
      },
    ]
  }

  useStickyTableHeader({
    wrapperRef: tableWrapperRef,
    tableRef: tableRef,
    enabled: !dataHasNotLoaded,
    dependencies: [columnWidths.join('|')], // ensure resync when columnWidths change
  });

  if (dataHasNotLoaded && classroom.id !== NO_CLASSROOM.id) {
    return (
      <div className="diagnostic-insights-content recommendations by-class">
        <Spinner />
      </div>
    )
  }

  if (classroom.id === NO_CLASSROOM.id || showEmptyState) {
    return (
      <EmptyState
        bodyText="There is no diagnostic history available for the filters selected, so no recommendations can be provided. Consider modifying a filter or assigning a diagnostic to your students. Once they complete an assessment, this report will become available."
        headerText="No diagnostic recommendations are available for this class"
      />
    )
  }

  function assignDiagnostics() {
    setAssigning(true)

    const selectedAssignmentsWithStudents = Object.values(selectedAssignments).filter(diagnostic => diagnostic.student_ids.length > 0)

    const params = {
      classroom_id: classroom.id,
      diagnostics: selectedAssignmentsWithStudents
    }

    requestPost('/teachers/progress_reports/assign_diagnostics/', params, (data) => {
      getPreviouslyAssignedDiagnosticDataForClassroom()
      setAssigning(false)
      setSnackbarText(`${pluralize(numberOfNewSelectedAssignments(), 'Diagnostic', 'Diagnostics')} assigned`)
      setShowSnackbar(true)
    }, (data) => {
      alert(TROUBLE_PROCESSING_MESSAGE);
      setAssigning(false)
    })
  }

  function deselectAll() {
    setSelectedAssignments({
      [STARTER]: {...selectedAssignments[STARTER], student_ids: [], },
      [INTERMEDIATE]: {...selectedAssignments[INTERMEDIATE], student_ids: [], },
      [ADVANCED]: {...selectedAssignments[ADVANCED], student_ids: [], }
    })
  }

  function selectAllRecommended() {
    setSelectedAssignments(recommendations)
  }

  const rows = studentHistories.map(studentHistory => {
    const { user_id, user_name, student_learning_sequences, } = studentHistory
    const { starter, intermediate, advanced, } = student_learning_sequences
    const nameAsArray = user_name.split(' ')
    const nameForSort = `${nameAsArray[nameAsArray.length - 1]} ${nameAsArray[0]}`
    const byStudentBaseUrl = `${window.location.href.split('/by_class')[0]}/by_student`
    const demonstratedProficiencyOnAllDiagnostics = proficientDiagnostic(starter) && proficientDiagnostic(intermediate) && proficientDiagnostic(advanced)

    const sharedStudentDiagnosticCellProps = { classroom, recommendations, selectedAssignments, setSelectedAssignments, studentHistory, previouslyAssignedDiagnosticsByLevel, useELLDiagnostics, }

    return {
      id: user_id,
      student: (
        <React.Fragment>
          <a className="student-link" href={`${byStudentBaseUrl}/${user_id}`}>{accountImg}{user_name}</a>
          {demonstratedProficiencyOnAllDiagnostics && (
            <Tooltip
              tooltipText={renderToString(
                <span>
                  {nameAsArray[0]} has demonstrated full proficiency in most skills on all diagnostics. While you can still assign a diagnostic, we recommend exploring additional content on Quill for continued growth.
                  <a href="https://support.quill.org/en/articles/10751323-how-do-teachers-use-diagnostic-insights" rel="noopener noreferrer" target="_blank">Learn more</a>
                </span>
              )}
              tooltipTriggerText={demonstratedProficiencyImg}
            />)}
        </React.Fragment>
      ),
      starter: <StudentDiagnosticCell {...sharedStudentDiagnosticCellProps} diagnosticLevel={STARTER} />,
      intermediate: <StudentDiagnosticCell {...sharedStudentDiagnosticCellProps} diagnosticLevel={INTERMEDIATE} />,
      advanced: <StudentDiagnosticCell {...sharedStudentDiagnosticCellProps} diagnosticLevel={ADVANCED} />,
      nameForSort,
    }
  })

  const numberOfNewSelectedAssignments = () => {
    let count = 0

    if (selectedAssignments) {
      Object.keys(selectedAssignments).forEach((diagnosticLevel) => {
        const assignment = selectedAssignments[diagnosticLevel]
        const diagnosticLevelForPreviousAssignments = keyForPreviouslyAssignedDiagnostics(diagnosticLevel, useELLDiagnostics)
        const relevantPreviouslyAssignedDiagnostics = previouslyAssignedDiagnosticsByLevel[diagnosticLevelForPreviousAssignments]
        const previouslyAssignedStudentIds = relevantPreviouslyAssignedDiagnostics?.map(previousAssignment => previousAssignment.user_id) || []
        count += assignment.student_ids.filter(id => !previouslyAssignedStudentIds.includes(id)).length
      })
    }

    return count
  }

  const anyAssignmentsSelected = numberOfNewSelectedAssignments() > 0

  const selectionButtonClassName = "quill-button grey outlined medium focus-on-light"

  return (
    <section className="diagnostic-insights-content recommendations by-class" ref={containerRef}>
      <Snackbar text={snackbarText} visible={showSnackbar} />
      <section className="key">
        {quillRecommendedImg}
        <h3>Recommended diagnostic</h3>
        <p>
          Students are recommended the next diagnostic after demonstrating full proficiency in most skills on the previous Pre or Post diagnostic
          <Tooltip
            tooltipText="Students qualify for the next diagnostic by demonstrating full proficiency (answering all questions correctly) in at least 80% of skills on the previous diagnostic level (Starter, Intermediate, Advanced). For example, on a 5-skill diagnostic, this means demonstrating full proficiency on 4 skills."
            tooltipTriggerText={helpImg}
          />
        </p>
      </section>
      <section className="assignment-bar">
        <div>
          <button className={selectionButtonClassName} onClick={selectAllRecommended} type="button">Select all recommended</button>
          <button className={selectionButtonClassName} onClick={deselectAll} type="button">Deselect all</button>
        </div>
        <button className={`quill-button green contained medium focus-on-light ${anyAssignmentsSelected ? '' : 'disabled'}`} disabled={!anyAssignmentsSelected} onClick={assignDiagnostics} type="button">
          {assigning ? <LightButtonLoadingSpinner /> : 'Assign diagnostics'}
        </button>
      </section>
      <div className="data-table-wrapper" ref={tableWrapperRef}>
        <DataTable
          headers={headers(useELLDiagnostics)}
          reportingFormat={true}
          rows={rows}
          tableRef={tableRef}
        />
      </div>
    </section>
  )
}

export default Recommendations
